import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, Banknote, FilePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";

const STATUS_STYLE = {
  paid: "bg-emerald-100 text-emerald-700",
  unpaid: "bg-red-100 text-red-700",
  pending: "bg-amber-100 text-amber-700",
};

export default function Finance() {
  const { t } = useLang();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [statusFilter, setStatusFilter] = useState("all");
  const isStaff = ["admin", "staff"].includes(user.role);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/invoices"), api.get("/invoices-summary")]);
    setInvoices(a.data);
    setSummary(b.data);
  };
  useEffect(() => { load().catch(() => toast.error(t("error"))); }, []);

  const generate = async () => {
    try {
      const r = await api.post("/invoices/generate-monthly", { month });
      toast.success(`${r.data.created} ${t("invoicesGenerated")}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const markPaid = async (inv) => {
    try {
      await api.post(`/invoices/${inv.invoice_id}/mark-paid`, { method: "cash" });
      toast.success(t("paidMarked"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const payStripe = async (inv) => {
    try {
      const r = await api.post("/payments/stripe/checkout", { invoice_id: inv.invoice_id, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const payToyyib = async (inv) => {
    try {
      const r = await api.post("/payments/toyyibpay/checkout", { invoice_id: inv.invoice_id, origin_url: window.location.origin });
      window.location.href = r.data.payment_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const filtered = invoices.filter((i) => statusFilter === "all" || i.status === statusFilter);

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-['Outfit'] text-2xl font-bold text-[#165A2E] md:text-3xl">{t("finance")}</h1>
        {isStaff && (
          <div className="flex items-center gap-2">
            <Input data-testid="generate-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" />
            <Button onClick={generate} data-testid="generate-monthly-btn" className="rounded-full bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D]">
              <FilePlus size={16} className="mr-1" /> {t("generateMonthly")}
            </Button>
          </div>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="gfa-card animate-fade-up p-6">
            <p className="text-sm text-slate-500">{t("outstanding")}</p>
            <p className="mt-1 font-['Outfit'] text-2xl font-bold text-red-600" data-testid="finance-outstanding">RM {summary.outstanding.toFixed(2)}</p>
          </div>
          <div className="gfa-card animate-fade-up stagger-1 p-6">
            <p className="text-sm text-slate-500">{t("collected")}</p>
            <p className="mt-1 font-['Outfit'] text-2xl font-bold text-[#165A2E]" data-testid="finance-collected">RM {summary.collected.toFixed(2)}</p>
          </div>
          <div className="gfa-card animate-fade-up stagger-2 p-6">
            <p className="text-sm text-slate-500">{t("unpaid")}</p>
            <p className="mt-1 font-['Outfit'] text-2xl font-bold text-amber-600" data-testid="finance-unpaid-count">{summary.count_unpaid}</p>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="invoice-status-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all")}</SelectItem>
            <SelectItem value="unpaid">{t("unpaid")}</SelectItem>
            <SelectItem value="pending">{t("pending")}</SelectItem>
            <SelectItem value="paid">{t("paid")}</SelectItem>
          </SelectContent>
        </Select>
        <p className="self-center text-xs text-slate-400">{t("paymentNote")}</p>
      </div>

      <div className="gfa-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("invoiceNo")}</TableHead>
              <TableHead>{t("player")}</TableHead>
              <TableHead>{t("description")}</TableHead>
              <TableHead>{t("type")}</TableHead>
              <TableHead>{t("amount")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead>{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((inv) => (
              <TableRow key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`}>
                <TableCell className="font-mono text-xs">{inv.number}</TableCell>
                <TableCell className="font-medium">{inv.player_name}</TableCell>
                <TableCell className="max-w-[220px] truncate text-sm text-slate-600">{inv.description}</TableCell>
                <TableCell className="capitalize">{t(inv.type)}</TableCell>
                <TableCell className="font-semibold">RM {Number(inv.amount).toFixed(2)}</TableCell>
                <TableCell>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLE[inv.status]}`} data-testid={`invoice-status-${inv.invoice_id}`}>
                    {t(inv.status)}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-2">
                    {isStaff && inv.status !== "paid" && (
                      <Button size="sm" variant="outline" onClick={() => markPaid(inv)} data-testid={`mark-paid-${inv.invoice_id}`} className="rounded-full text-xs">
                        <CheckCircle2 size={13} className="mr-1" /> {t("markPaid")}
                      </Button>
                    )}
                    {user.role === "parent" && inv.status === "unpaid" && (
                      <>
                        <Button size="sm" onClick={() => payStripe(inv)} data-testid={`pay-stripe-${inv.invoice_id}`} className="rounded-full bg-[#165A2E] text-xs text-white hover:bg-[#114724]">
                          <CreditCard size={13} className="mr-1" /> {t("payStripe")}
                        </Button>
                        <Button size="sm" onClick={() => payToyyib(inv)} data-testid={`pay-toyyib-${inv.invoice_id}`} className="rounded-full bg-[#84CC16] text-xs text-[#0A0A0A] hover:bg-[#65A30D]">
                          <Banknote size={13} className="mr-1" /> {t("payToyyib")}
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-slate-400">{t("noData")}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
